import React, { useMemo } from "react";
import { styled } from "@mui/material/styles";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import type { SegmentedControlProps } from "./SegmentedControl.types";
import { useAnimatedBubble } from "./hooks/useAnimatedBubble";
import Box from "@mui/material/Box"

/**
 * Styled container for the SegmentedControl component
 */
const StyledContainer = styled(Box)({
  position: "relative",
  display: "inline-flex",
});

/**
 * Styled bubble indicator with animation support
 */
const StyledBubble = styled("div")<{
  width: string;
  transform: string;
  opacity: number;
}>(({ theme, width, transform, opacity }) => ({
  position: "absolute",
  top: 3,
  left: 0,
  height: 34,
  backgroundColor: theme.palette.background.paper,
  borderRadius: 10,
  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
  zIndex: 1,
  width: width,
  transform: transform,
  opacity: opacity,
  boxShadow:
    "0px 1px 3px 0px $palette-common-background, 0px 1px 2px -1px $palette-common-background",
}));

/**
 * Styled wrapper for ToggleButtonGroup with custom styling
 */
const StyledToggleButtonGroup = styled(ToggleButtonGroup)(({ theme }) => ({
  position: "relative",
  backgroundColor: theme.palette.background.default,
  borderRadius: 12,
  width: "100%",
  borderWidth: 3,
  borderStyle: "solid",
  borderColor: theme.palette.background.default,

  "& .MuiToggleButton-root": {
    minHeight: 34,
    fontSize: theme.typography.body1.fontSize,
    padding: 0,
    position: "relative",
    zIndex: 1,
    backgroundColor: "transparent",

    "&.Mui-selected": {
      backgroundColor: "transparent",
    },
  },
}));

/**
 * Styled individual toggle button with theme integration
 */
const StyledToggleButton = styled(ToggleButton)(({ theme }) => ({
  flex: 1,
  fontWeight: 500,
  textTransform: "none",
  color: theme.palette.text.primary,
  border: "none",
  "&:hover, &.Mui-selected:hover": {
    backgroundColor: "transparent",
  },
}));

/**
 * SegmentedControl component with animated sliding indicator
 *
 * A modern segmented control component that displays a set of options as toggleable buttons
 * with a smooth sliding animation indicator that moves to the active selection.
 *
 * @template T - Type of option values (defaults to string)
 * @param props - Component properties
 * @returns JSX element
 *
 * @example
 * ```tsx
 * <SegmentedControl
 *   value="option1"
 *   onChange={(value) => setValue(value)}
 *   options={[
 *     { value: "option1", label: "Option 1" },
 *     { value: "option2", label: "Option 2" },
 *   ]}
 *   fullWidth
 * />
 * ```
 */
const SegmentedControlComponent = <T extends string = string>({
  value,
  onChange,
  options,
  exclusive = true,
  fullWidth = false,
  disabled = false,
  className,
  "data-testid": dataTestId,
  ...props
}: SegmentedControlProps<T>) => {
  // Custom hook for animated bubble logic
  const { containerRef, bubbleStyle } = useAnimatedBubble({
    value,
    options,
  });

  /**
   * Handle toggle button group change
   */
  const handleChange = (
    _: React.MouseEvent<HTMLElement>,
    newValue: T | T[]
  ) => {
    if (newValue !== null) {
      onChange(newValue);
    }
  };

  /**
   * Memoized button elements for performance optimization
   */
  const buttonElements = useMemo(
    () =>
      options.map((option) => (
        <StyledToggleButton
          key={String(option.value)}
          disableRipple
          value={option.value}
          disabled={option.disabled || disabled}
        >
          {option.label}
        </StyledToggleButton>
      )),
    [options, disabled]
  );

  return (
    <StyledContainer
      ref={containerRef}
      className={className}
      sx={{
        width: fullWidth ? "100%" : "auto",
      }}
      data-testid={dataTestId}
    >
      {/* Animated bubble indicator */}
      <StyledBubble
        width={bubbleStyle.width}
        transform={bubbleStyle.transform}
        opacity={bubbleStyle.opacity}
      />

      {/* Toggle button group */}
      <StyledToggleButtonGroup
        value={value}
        exclusive={exclusive}
        onChange={handleChange}
        disabled={disabled}
        {...props}
      >
        {buttonElements}
      </StyledToggleButtonGroup>
    </StyledContainer>
  );
};

/**
 * Memoized SegmentedControl component for performance optimization
 * Prevents unnecessary re-renders when parent components update
 */
export const SegmentedControl = React.memo(
  SegmentedControlComponent
) as typeof SegmentedControlComponent;

// Set display name for debugging and React DevTools
(SegmentedControl as any).displayName = "SegmentedControl";
